import React, {useEffect, useState} from 'react';
import Question from "../components/form/Question";
import Title from "../components/Title";
import {useSearchParams} from "react-router-dom";
import axios from "axios";
import {useConfig} from "../components/providers/ConfigProvider";
import * as styles from "../styles";
import ErrorPage from "../components/ErrorPage";
import {useSections} from "../components/providers/SectionsProvider";

const Form = () => {
  const {proxyUrl, proxyPort} = useConfig();
  const {sections, isMobile} = useSections();

  const [params] = useSearchParams();
  const index = Number(params.get('section')) || 0;
  const sectionConfig = sections[index] || null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const formId = sectionConfig?.formId || "";
  const basePath = formId.startsWith("1FAIp") ? "d/e" : "d";
  const link = `https://docs.google.com/forms/${basePath}/${formId}`;

  const [submitUrl, setSubmitUrl] = useState(`${link}/formResponse`);
  const [entries, setEntries] = useState<string[]>([]);
  const [alerts, setAlerts] = useState<boolean[]>([]);
  const [selections, setSelections] = useState<string[]>([]);

  useEffect(() => {
    if (!sectionConfig || !sectionConfig.questions) return;

    const extractEntries = async () => {
      try {
        const response = await axios.get(`${proxyUrl}:${proxyPort}/proxy?url=${link}/viewform`);
        const html = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);

        // Extract action URL
        const actionMatch = html.match(/action="([^"]+formResponse)"/i);
        if (actionMatch) {
          setSubmitUrl(actionMatch[1]);
        } else {
          setSubmitUrl(`${link}/formResponse`);
        }

        // Parse native metadata block
        const match = html.match(/FB_PUBLIC_LOAD_DATA_\s*=\s*([\s\S]*?);?\s*<\/script>/);
        if (!match) throw new Error("Metadata not found in raw HTML.");

        const rawData = JSON.parse(match[1].trim());
        const formItems = rawData[1]?.[1] || [];

        const mappedEntries: string[] = new Array(sectionConfig.questions.length).fill("");

        formItems.forEach((q: any) => {
          const title = q[1]; 
          const entryId = q[4]?.[0]?.[0]; 
          
          if (title && entryId) {
            const titleMatch = title.match(/\[(\d+)\]/);
            if (titleMatch) {
              const qIndex = parseInt(titleMatch[1], 10);
              if (qIndex < sectionConfig.questions.length) {
                mappedEntries[qIndex] = `entry.${entryId}`;
              }
            }
          }
        });

        setEntries(mappedEntries);
      } catch (error) {
        console.error("Failed to parse form HTML:", error);
        setEntries([]);
      }
    };
    
    extractEntries();
  }, [proxyPort, proxyUrl, index, sections.length, link, sectionConfig]);

  useEffect(() => {
    setAlerts(entries.map(() => false));
    setSelections(entries.map(() => ''));
    setLoading(entries.length === 0 || entries.includes(""));
  }, [entries]);

  /*const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const missingRequired = selections.some((value, i) => sectionConfig.questions[i].required && value === '');
    
    if (missingRequired) {
      e.preventDefault(); 
      setAlerts(selections.map((value, i) => sectionConfig.questions[i].required && value.length === 0));
      window.scrollTo(0, 0);
      return;
    }
    
    setSubmitting(true);
  };*/

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Package your mapped entry parameters { "entry.12345": "Yes", ... }
    const payloadEntries: Record<string, string> = {};
    entries.forEach((entry, i) => {
      if (entry && selections[i]) {
        payloadEntries[entry] = selections[i];
      }
    });

    setSubmitting(true);

    try {
      const response = await fetch(`${proxyUrl}:${proxyPort}/proxy-post`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submitResponses",
          formId: formId,
          responses: payloadEntries
        })
      });

      const data = await response.json();
      if (data.scoreUrl) {
        // Navigates directly to the native Google Forms "View Score" landing page
        window.location.href = data.scoreUrl;
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setSubmitting(false);
    }
  };
  if (!sectionConfig) return <ErrorPage/>;

  if (loading) {
    return (
      <div style={styles.bg}>
        <div style={{...styles.form, ...(isMobile ? styles.scaleMobile : styles.scaleNormal)}}>
          <Title title="Loading..." subTitle={"Please wait"} description={""}/>
          <div style={{width: 640}}/>
        </div>
      </div>
    );
  }

  return (
    <div className="App" style={styles.bg}>
      <form 
        action={submitUrl} 
        method="POST" 
        onSubmit={handleFormSubmit}
        style={{...styles.form, ...(isMobile ? styles.scaleMobile : styles.scaleNormal)}}
      >
        <Title
          title={sectionConfig.title}
          secondaryTitle={""}
          subTitle={sectionConfig.subtitle}
          description={sectionConfig.description}
          theme={sectionConfig.theme}
          alert={alerts.some((value) => value)}
        />
        
        <input type="hidden" name="fvv" value="1" />
        <input type="hidden" name="pageHistory" value="0" />

        {/* Dynamic Inputs */}
        {entries.map((entry, i) => {
          if (!entry || selections[i] === '') return null;

          const isChoiceQuestion = sectionConfig.questions[i]?.type === 'choice';

          return (
            <React.Fragment key={`group-${i}`}>
              {/* Primary answer entry */}
              <input type="hidden" name={entry} value={selections[i]} />
              
              {/* 🎯 THE FIX: Inject the sentinel parameter Google Forms requires for choice items */}
              {isChoiceQuestion && (
                <input type="hidden" name={`${entry}_sentinel`} value="" />
              )}
            </React.Fragment>
          );
        })}

        <div>
          {entries.map((entry, i) =>
            <Question
              index={i}
              entry={entry}
              section={sectionConfig}
              alert={alerts[i]}
              setAlert={(value) => {
                const newAlerts = [...alerts];
                newAlerts[i] = value;
                setAlerts(newAlerts);
              }}
              selection={selections[i]}
              setSelection={(value) => {
                const newSelections = [...selections];
                newSelections[i] = value;
                setSelections(newSelections);
              }}
              key={i}
            />
          )}
        </div>

        <div style={styles.footer}>
          <button type="submit" disabled={submitting} style={{...styles.button, backgroundColor: sectionConfig.theme}}>
            <div style={styles.submit}>{submitting ? "Submitting..." : "Submit"}</div>
          </button>
          
          <div
            style={{...styles.clearForm, color: sectionConfig.theme}}
            onClick={() => {
              if (submitting) return;
              setSelections(entries.map(() => ''));
              setAlerts(entries.map(() => false));
              window.scrollTo(0, 0);
            }}>
            Clear Form
          </div>
        </div>
      </form>
    </div>
  );
}

export default Form;